/**
 * FunnelStagesCard (KAN-737, shared since KAN-744)
 *
 * Funnel block: the cohort of clients that entered the funnel during the
 * period, per stage: reached, here now, moved on, refused, conversion and
 * refusal percent of the whole cohort. Period and channel tabs, legend.
 * The data source is a prop: the dashboard passes the org-wide
 * /analytics/funnel/stages/, the manager card passes /managers/<id>/funnel/
 * (same response format). `clientsParams` are added to the clients-list links
 * of the segments (manager card: `manager=<id>`).
 * Channel: the channel of the client's first contact (calls = voice, outreach = the rest).
 * Rows: the cold entry branch (cold -> replied), the inbound entry, then the
 * shared stages, each group by stage order.
 */
import { useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import { Filter, Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { InfoTooltip } from '@/components/ui/info-tooltip'
import { useFunnelStageStats, type FunnelStageStatsLoader } from '@/hooks/useFunnelStageStats'
import type { FunnelStageStats, FunnelStatsChannel, FunnelStatsPeriod } from '@/types/funnel'
import { PillTabs, type PillTab } from './PillTabs'
import { FunnelStageRow } from './FunnelStageRow'
import { SEGMENT_COLORS } from './funnelStageFormat'

const PERIOD_TABS: readonly PillTab<FunnelStatsPeriod>[] = [
  { value: '1d', label: '1 день' },
  { value: '7d', label: '7 дней' },
  { value: '30d', label: '30 дней' },
  { value: 'all', label: 'Всё время' },
]

const DEFAULT_PERIOD: FunnelStatsPeriod = '30d'

const CHANNEL_TABS: readonly PillTab<FunnelStatsChannel>[] = [
  { value: 'outreach', label: 'Рассылка' },
  { value: 'calls', label: 'Звонки' },
  { value: 'all', label: 'Все' },
]

const DEFAULT_CHANNEL: FunnelStatsChannel = 'outreach'

const TITLE_HINT =
  'Дошли: сколько клиентов дошли до этапа и какая это доля от всех вошедших за период. ' +
  'Отказ: сколько отказались на этом этапе (вручную или ботом) и доля от всех вошедших. ' +
  'Цвета полосы: ушли дальше, сейчас здесь, отказ.'

const LEGEND: { key: keyof typeof SEGMENT_COLORS; label: string }[] = [
  { key: 'moved_on', label: 'ушли дальше' },
  { key: 'here', label: 'сейчас здесь' },
  { key: 'refused', label: 'отказ' },
]

const GROUP_RANK: Record<string, number> = { cold: 0, inbound: 1 }

/** Entry branches first (cold, then inbound), then the shared stages; by order inside a group. */
function orderStages(stages: FunnelStageStats[]): FunnelStageStats[] {
  const rank = (s: FunnelStageStats) => (s.entry_group ? GROUP_RANK[s.entry_group] ?? 2 : 3)
  return [...stages].sort((a, b) => rank(a) - rank(b) || a.order - b.order)
}

function Legend() {
  return (
    <div data-testid="funnel-legend" className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
      {LEGEND.map((item) => (
        <span key={item.key} className="flex items-center gap-1.5">
          <span className={`h-2.5 w-2.5 rounded-sm ${SEGMENT_COLORS[item.key]}`} />
          {item.label}
        </span>
      ))}
    </div>
  )
}

function Empty({ text }: { text: string }) {
  return (
    <div data-testid="funnel-empty" className="flex flex-col items-center justify-center py-8 text-gray-400">
      <Filter className="mb-2 h-7 w-7" />
      <p className="text-sm">{text}</p>
    </div>
  )
}

interface FunnelStagesCardProps {
  title: string
  /** Query key prefix; period and channel are appended. */
  queryKey: readonly unknown[]
  load: FunnelStageStatsLoader
  /** Extra filters of the clients-list links of the segments. */
  clientsParams?: Record<string, string>
  testId?: string
}

export function FunnelStagesCard({
  title,
  queryKey,
  load,
  clientsParams,
  testId = 'funnel-card',
}: FunnelStagesCardProps) {
  const [period, setPeriod] = useState<FunnelStatsPeriod>(DEFAULT_PERIOD)
  const [channel, setChannel] = useState<FunnelStatsChannel>(DEFAULT_CHANNEL)
  const { data, isLoading, error } = useFunnelStageStats(queryKey, load, period, channel)
  const stages = useMemo(() => orderStages(data?.stages ?? []), [data])
  const maxReached = Math.max(0, ...stages.map((s) => s.reached))

  let body: JSX.Element
  if (isLoading) {
    body = (
      <div data-testid="funnel-loading" className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    )
  } else if (error) {
    const forbidden = isAxiosError(error) && error.response?.status === 403
    body = (
      <div data-testid="funnel-error" className="py-8 text-center">
        <p className="text-sm font-medium text-red-600">
          {forbidden ? 'Нет доступа к аналитике воронки' : 'Не удалось загрузить воронку'}
        </p>
        {!forbidden && <p className="mt-1 text-xs text-gray-500">Попробуйте позже</p>}
      </div>
    )
  } else if (!data?.funnel) {
    body = <Empty text="Воронка не настроена" />
  } else if (data.cohort === 0 || stages.length === 0) {
    body = <Empty text="За период в воронку никто не вошёл" />
  } else {
    const funnel = data.funnel.codename
    body = (
      <div className="space-y-0.5">
        {stages.map((stage) => (
          <FunnelStageRow
            key={stage.codename}
            stage={stage}
            funnel={funnel}
            maxReached={maxReached}
            clientsParams={clientsParams}
          />
        ))}
      </div>
    )
  }

  return (
    <Card data-testid={testId}>
      <CardHeader className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <CardTitle>{title}</CardTitle>
            <InfoTooltip text={TITLE_HINT} />
          </div>
          {data && data.cohort > 0 && (
            <span className="text-sm text-gray-500">
              вошли за период <span className="font-semibold tabular-nums text-gray-900">{data.cohort}</span>
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <PillTabs label="Канал" tabs={CHANNEL_TABS} value={channel} onChange={setChannel} testIdPrefix="funnel-channel" />
          <PillTabs label="Период" tabs={PERIOD_TABS} value={period} onChange={setPeriod} testIdPrefix="funnel-period" />
        </div>
        <Legend />
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  )
}
